import { auth } from "./firebase";
import type {
  Attachment,
  AttachmentKind,
  ChecklistItem,
  ChecklistItemStatus,
  Deal,
  DealType,
  DealVisibility,
  DocumentVersion,
  Message,
  PublicShareDeal,
  PublicShareInfo,
  PublicShareMessage,
  RepoCustomSection,
  RepoDocKind,
  RepoDocKindDef,
  RepoEntity,
  RepoMessage,
  RepoBridgeFlag,
  RepoMetadataValue,
  RepoSchema,
  RepoSchemaField,
  RepoTranche,
  RepositoryDeal,
  RepositoryDoc,
  Review,
  ReviewSectionKey,
  ReviewStage,
  ReviewType,
  ShareLink,
  TeamConvention,
  UserProfile,
} from "@docreview/shared";

export interface RedlineDocRef {
  id: string;
  filename: string;
}

export interface RedlinePairProposal {
  pairs: {
    oursId: string;
    oursFilename: string;
    theirsId: string;
    theirsFilename: string;
    score: number;
  }[];
  unpairedOurs: RedlineDocRef[];
  unpairedTheirs: RedlineDocRef[];
  oursDocx: RedlineDocRef[];
  theirsDocx: RedlineDocRef[];
}

export interface RedlineCreateResult {
  oursId: string;
  theirsId: string;
  theirsFilename: string;
  ok: boolean;
  // Absent when ok && revisionCount === 0 — identical docs produce no file.
  attachment?: Attachment;
  revisionCount?: number;
  error?: string;
}

export interface RedlineJobFile {
  fileId: string;
  token: string;
  filename: string;
}

export interface RedlineJobResult {
  index: number;
  theirsFilename: string;
  ok: boolean;
  hasOutput: boolean;
  outputFilename?: string;
  revisionCount?: number;
  error?: string;
}

export interface RedlineJobStatus {
  id: string;
  state: "running" | "done" | "canceled";
  total: number;
  completed: number;
  results: RedlineJobResult[];
}

async function authHeader(): Promise<HeadersInit> {
  const u = auth.currentUser;
  if (!u) throw new Error("not_signed_in");
  const token = await u.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

async function jsonRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers ?? {});
  const auth = await authHeader();
  for (const [k, v] of Object.entries(auth)) headers.set(k, v as string);
  if (init.body && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(path, { ...init, headers });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status} ${text}`);
  }
  return (await res.json()) as T;
}

export const api = {
  me: () =>
    jsonRequest<{ user: UserProfile; isAdmin: boolean; canCreatePrivateDeals?: boolean }>(
      "/api/me",
    ),

  listDeals: () => jsonRequest<{ deals: Deal[] }>("/api/deals"),
  createDeal: (body: {
    title: string;
    ticker?: string;
    dealType: DealType;
    notes?: string;
    // Only accepted from users on the backend's PRIVATE_DEAL_EMAILS list;
    // anyone else gets a 403 (and never sees the control that sends it).
    visibility?: DealVisibility;
  }) =>
    jsonRequest<{ deal: Deal }>("/api/deals", { method: "POST", body: JSON.stringify(body) }),
  getDeal: (id: string) => jsonRequest<{ deal: Deal }>(`/api/deals/${id}`),
  patchDeal: (
    id: string,
    body: Partial<Pick<Deal, "title" | "ticker" | "dealType" | "notes">>,
  ) => jsonRequest<{ deal: Deal }>(`/api/deals/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteDeal: (id: string) =>
    jsonRequest<{ ok: true }>(`/api/deals/${id}`, { method: "DELETE" }),

  listReviews: (dealId: string) =>
    jsonRequest<{ reviews: Review[] }>(`/api/reviews/by-deal/${dealId}`),
  createReview: (
    dealId: string,
    body: {
      title?: string;
      // New canonical input: reviewType + versionId (+ priorVersionId for redline).
      reviewType?: ReviewType;
      versionId?: string;
      priorVersionId?: string;
      // Legacy field still accepted for any old caller paths.
      reviewStage?: ReviewStage;
      skipSections?: ReviewSectionKey[];
      quickPass?: boolean;
    },
  ) =>
    jsonRequest<{ review: Review }>(`/api/reviews/by-deal/${dealId}`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  getReview: (dealId: string, reviewId: string) =>
    jsonRequest<{ review: Review }>(`/api/reviews/${dealId}/${reviewId}`),
  patchReview: (
    dealId: string,
    reviewId: string,
    body: Partial<Pick<Review, "title" | "reviewStage" | "status">>,
  ) =>
    jsonRequest<{ review: Review }>(`/api/reviews/${dealId}/${reviewId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteReview: (dealId: string, reviewId: string) =>
    jsonRequest<{ ok: true }>(`/api/reviews/${dealId}/${reviewId}`, { method: "DELETE" }),
  // Reset a done-but-empty review so the next message re-runs the full
  // review (recovery from a truncated run). 409s if items already exist.
  rerunReview: (dealId: string, reviewId: string) =>
    jsonRequest<{ ok: true }>(`/api/reviews/${dealId}/${reviewId}/rerun`, { method: "POST" }),

  listVersions: (dealId: string) =>
    jsonRequest<{ versions: DocumentVersion[] }>(`/api/versions/by-deal/${dealId}`),
  createVersion: (dealId: string, label: string) =>
    jsonRequest<{ version: DocumentVersion }>(`/api/versions/by-deal/${dealId}`, {
      method: "POST",
      body: JSON.stringify({ label }),
    }),
  renameVersion: (dealId: string, versionId: string, label: string) =>
    jsonRequest<{ version: DocumentVersion }>(`/api/versions/${dealId}/${versionId}`, {
      method: "PATCH",
      body: JSON.stringify({ label }),
    }),
  deleteVersion: (dealId: string, versionId: string) =>
    jsonRequest<{ ok: true }>(`/api/versions/${dealId}/${versionId}`, {
      method: "DELETE",
    }),

  // Redline Creator: compare the .docx files of two versions and generate
  // Word tracked-changes redlines into a new version.
  proposeRedlinePairs: (dealId: string, oursVersionId: string, theirsVersionId: string) =>
    jsonRequest<RedlinePairProposal>(`/api/redlines/${dealId}/propose`, {
      method: "POST",
      body: JSON.stringify({ oursVersionId, theirsVersionId }),
    }),
  createRedlines: (
    dealId: string,
    body: {
      oursVersionId: string;
      theirsVersionId: string;
      author?: string;
      pairs: { oursId: string; theirsId: string }[];
    },
  ) =>
    jsonRequest<{ version: DocumentVersion | null; results: RedlineCreateResult[] }>(
      `/api/redlines/${dealId}/create`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  // Standalone Redline Creator (background jobs over ad-hoc uploads).
  uploadRedlineJobFile: async (file: File): Promise<RedlineJobFile> => {
    const sign = await jsonRequest<{
      fileId: string;
      token: string;
      uploadUrl: string;
      uploadHeaders: Record<string, string>;
    }>("/api/redline-jobs/sign", {
      method: "POST",
      body: JSON.stringify({ filename: file.name, sizeBytes: file.size }),
    });
    const putRes = await fetch(sign.uploadUrl, {
      method: "PUT",
      headers: sign.uploadHeaders,
      body: file,
    });
    if (!putRes.ok) throw new Error(`upload failed (${putRes.status})`);
    return { fileId: sign.fileId, token: sign.token, filename: file.name };
  },
  proposeRedlineJobPairs: (oursTokens: string[], theirsTokens: string[]) =>
    jsonRequest<{
      pairs: { oursId: string; theirsId: string; score: number }[];
      unpairedOurs: string[];
      unpairedTheirs: string[];
    }>("/api/redline-jobs/propose", {
      method: "POST",
      body: JSON.stringify({ oursTokens, theirsTokens }),
    }),
  createRedlineJob: (body: { author?: string; pairs: { oursToken: string; theirsToken: string }[] }) =>
    jsonRequest<{ jobId: string; total: number }>("/api/redline-jobs", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  getRedlineJob: (jobId: string) =>
    jsonRequest<RedlineJobStatus>(`/api/redline-jobs/${jobId}`),
  redlineJobFileUrl: (jobId: string, index: number) =>
    jsonRequest<{ url: string }>(`/api/redline-jobs/${jobId}/file/${index}`),
  // Plain call cancels a running job (outputs kept); {discard: true}
  // additionally deletes a finished job's outputs ("Start over").
  cancelRedlineJob: (jobId: string, opts?: { discard?: boolean }) =>
    jsonRequest<{ ok: true }>(`/api/redline-jobs/${jobId}${opts?.discard ? "?discard=true" : ""}`, {
      method: "DELETE",
    }),
  downloadRedlineJobZip: async (jobId: string): Promise<Blob> => {
    const headers = new Headers(await authHeader());
    const res = await fetch(`/api/redline-jobs/${jobId}/zip`, { headers });
    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
    return res.blob();
  },

  listAttachments: (dealId: string) =>
    jsonRequest<{ attachments: Attachment[] }>(`/api/upload/by-deal/${dealId}`),
  uploadAttachment: async (
    dealId: string,
    file: File,
    kind: AttachmentKind,
    versionId?: string,
  ) => {
    // Two-step flow: ask the backend for a signed GCS PUT URL, send
    // the bytes straight to GCS, then tell the backend we're done.
    // The file never traverses our Railway proxy, which used to kill
    // large multipart uploads mid-flight.
    const sign = await jsonRequest<{
      attachmentId: string;
      uploadUrl: string;
      contentType: string;
      uploadToken: string;
      uploadHeaders: Record<string, string>;
    }>(`/api/upload/${dealId}/sign`, {
      method: "POST",
      body: JSON.stringify({
        kind,
        versionId,
        filename: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
      }),
    });
    // Headers must match byte-for-byte what the backend signed —
    // including x-goog-content-length-range, which lets GCS reject
    // oversized PUTs at the cloud layer instead of letting them land.
    const putRes = await fetch(sign.uploadUrl, {
      method: "PUT",
      headers: sign.uploadHeaders,
      body: file,
    });
    if (!putRes.ok) {
      const body = await putRes.text().catch(() => "");
      throw new Error(`upload_failed ${putRes.status}: ${body.slice(0, 200)}`);
    }
    return jsonRequest<{ attachment: Attachment }>(
      `/api/upload/${dealId}/complete`,
      { method: "POST", body: JSON.stringify({ uploadToken: sign.uploadToken }) },
    );
  },
  deleteAttachment: (id: string) =>
    jsonRequest<{ ok: true }>(`/api/upload/${id}`, { method: "DELETE" }),

  listMessages: (dealId: string, reviewId: string) =>
    jsonRequest<{ messages: Message[] }>(`/api/messages/${dealId}/${reviewId}`),

  listChecklist: (dealId: string, reviewId: string) =>
    jsonRequest<{ items: ChecklistItem[] }>(`/api/checklist/${dealId}/${reviewId}`),
  updateChecklistItem: (
    dealId: string,
    reviewId: string,
    itemId: string,
    body: { status?: ChecklistItemStatus | null; comment?: string },
  ) =>
    jsonRequest<{ item: ChecklistItem }>(
      `/api/checklist/${dealId}/${reviewId}/${itemId}`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),
  submitChecklist: (dealId: string, reviewId: string) =>
    jsonRequest<{ ok: true; nextReview: Review | null }>(
      `/api/checklist/${dealId}/${reviewId}/submit`,
      { method: "POST", body: "{}" },
    ),

  getAttachmentUrl: (attachmentId: string) =>
    jsonRequest<{ url: string; filename: string }>(`/api/upload/${attachmentId}/url`),

  /**
   * Streams a zip of a deal's attachments (optionally filtered by kind
   * and/or version, or the special "legacy unversioned" bucket) and
   * triggers a browser download. Uses fetch + blob because the
   * response is a streamed zip — we can't pre-sign a URL the way we
   * do for individual files.
   */
  downloadAttachmentsZip: async (
    dealId: string,
    opts: {
      kind?: AttachmentKind;
      versionId?: string;
      onlyUnversionedDocs?: boolean;
    } = {},
  ) => {
    const headers = new Headers(await authHeader());
    const params = new URLSearchParams();
    if (opts.kind) params.set("kind", opts.kind);
    if (opts.versionId) params.set("versionId", opts.versionId);
    if (opts.onlyUnversionedDocs) params.set("onlyUnversionedDocs", "true");
    const qs = params.toString() ? `?${params.toString()}` : "";
    const res = await fetch(`/api/upload/zip/${dealId}${qs}`, { headers });
    if (!res.ok) {
      const text = await res.text();
      let pretty = text;
      try {
        const parsed = JSON.parse(text);
        if (parsed && typeof parsed.message === "string") pretty = parsed.message;
        else if (parsed && typeof parsed.error === "string") pretty = parsed.error;
      } catch {
        // not JSON
      }
      throw new Error(`${res.status} ${pretty}`);
    }
    // Try to pull the server-provided filename out of Content-Disposition.
    const cd = res.headers.get("content-disposition") ?? "";
    const match = cd.match(/filename="([^"]+)"/i);
    const filename = match?.[1] ?? "documents.zip";
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  },

  listShares: (dealId: string, reviewId: string) =>
    jsonRequest<{ shares: Omit<ShareLink, "passwordHash">[] }>(
      `/api/shares/by-review/${dealId}/${reviewId}`,
    ),
  createShare: (body: {
    dealId: string;
    reviewId: string;
    password: string;
    expiresInHours: number;
  }) =>
    jsonRequest<{ share: Omit<ShareLink, "passwordHash"> }>("/api/shares", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  revokeShare: (id: string) =>
    jsonRequest<{ ok: true }>(`/api/shares/${id}`, { method: "DELETE" }),

  listConventions: () =>
    jsonRequest<{ conventions: TeamConvention[]; isAdmin: boolean }>("/api/conventions"),
  flagItemAsConvention: (
    dealId: string,
    reviewId: string,
    itemId: string,
    body: { reason: string; title?: string },
  ) =>
    jsonRequest<{ convention: TeamConvention }>(
      `/api/conventions/from-item/${dealId}/${reviewId}/${itemId}`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  patchConvention: (
    id: string,
    body: { title?: string; rationale?: string },
  ) =>
    jsonRequest<{ convention: TeamConvention }>(`/api/conventions/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  approveConvention: (id: string) =>
    jsonRequest<{ convention: TeamConvention }>(`/api/conventions/${id}/approve`, {
      method: "POST",
      body: "{}",
    }),
  rejectConvention: (id: string, reason?: string) =>
    jsonRequest<{ convention: TeamConvention }>(`/api/conventions/${id}/reject`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),
  revokeConvention: (id: string, reason?: string) =>
    jsonRequest<{ convention: TeamConvention }>(`/api/conventions/${id}/revoke`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  // Admin
  verifyUserEmail: (email: string) =>
    jsonRequest<{ ok: true; email: string; alreadyVerified: boolean }>(
      "/api/admin/verify-user",
      { method: "POST", body: JSON.stringify({ email }) },
    ),
  migrateToReviewsDryRun: () =>
    jsonRequest<{ dryRun: true; plan: unknown }>("/api/admin/migrate-to-reviews?dryRun=true", {
      method: "POST",
      body: "{}",
    }),
  migrateToReviewsApply: () =>
    jsonRequest<{ dryRun: false; plan: unknown; log: string[] }>(
      "/api/admin/migrate-to-reviews?dryRun=false",
      { method: "POST", body: "{}" },
    ),
  migrateToVersionsDryRun: () =>
    jsonRequest<{
      dryRun: true;
      reviewsBackfilled: number;
      versionsCreated: number;
      attachmentsLinked: number;
      log: string[];
    }>("/api/admin/migrate-to-versions?dryRun=true", {
      method: "POST",
      body: "{}",
    }),
  migrateToVersionsApply: () =>
    jsonRequest<{
      dryRun: false;
      reviewsBackfilled: number;
      versionsCreated: number;
      attachmentsLinked: number;
      log: string[];
    }>("/api/admin/migrate-to-versions?dryRun=false", {
      method: "POST",
      body: "{}",
    }),

  // ----- Document Repository -------------------------------------------
  getRepoSchema: () => jsonRequest<{ schema: RepoSchema }>("/api/repository/schema"),

  listRepoEntities: () => jsonRequest<{ entities: RepoEntity[] }>("/api/repository/entities"),
  addRepoEntity: (name: string) =>
    jsonRequest<{ entity: RepoEntity; added: boolean }>("/api/repository/entities", {
      method: "POST",
      body: JSON.stringify({ name }),
    }),
  listRepoDocKinds: () => jsonRequest<{ kinds: RepoDocKindDef[] }>("/api/repository/doc-kinds"),
  addRepoDocKind: (body: { label: string; isAmendment?: boolean }) =>
    jsonRequest<{ kind: RepoDocKindDef; added: boolean }>("/api/repository/doc-kinds", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  patchRepoDocKind: (key: string, body: { label?: string; isAmendment?: boolean }) =>
    jsonRequest<{ kind: RepoDocKindDef; dealsRecounted: number }>(
      `/api/repository/doc-kinds/${encodeURIComponent(key)}`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),
  deleteRepoDocKind: (key: string) =>
    jsonRequest<{ ok: true }>(`/api/repository/doc-kinds/${encodeURIComponent(key)}`, {
      method: "DELETE",
    }),
  addRepoField: (body: Partial<RepoSchemaField>) =>
    jsonRequest<{ field: RepoSchemaField }>("/api/repository/schema/fields", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  patchRepoField: (key: string, body: Partial<RepoSchemaField>) =>
    jsonRequest<{ field: RepoSchemaField }>(`/api/repository/schema/fields/${key}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  listRepoDeals: (status?: string) =>
    jsonRequest<{ deals: RepositoryDeal[] }>(
      `/api/repository/deals${status ? `?status=${encodeURIComponent(status)}` : ""}`,
    ),
  createRepoDeal: (body: {
    ticker: string;
    dealNumber?: number;
    companyName?: string;
    lenderEntity?: string;
    closingDate?: number;
  }) =>
    jsonRequest<{ deal: RepositoryDeal }>("/api/repository/deals", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  getRepoDeal: (id: string) =>
    jsonRequest<{ deal: RepositoryDeal }>(`/api/repository/deals/${id}`),
  patchRepoDeal: (
    id: string,
    body: Partial<{
      companyName: string;
      lenderEntity: string;
      ticker: string;
      dealNumber: number;
      closingDate: number | null;
    }>,
  ) =>
    jsonRequest<{ deal: RepositoryDeal }>(`/api/repository/deals/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  patchRepoDealMetadata: (
    id: string,
    body: {
      metadata?: Record<string, Partial<RepoMetadataValue>>;
      tranches?: RepoTranche[];
      customSections?: RepoCustomSection[];
    },
  ) =>
    jsonRequest<{ deal: RepositoryDeal }>(`/api/repository/deals/${id}/metadata`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  promoteRepoSection: (label: string) =>
    jsonRequest<{ field: RepoSchemaField; migrated: number }>(
      "/api/repository/schema/promote-section",
      { method: "POST", body: JSON.stringify({ label }) },
    ),
  commitRepoDeal: (id: string) =>
    jsonRequest<{ deal: RepositoryDeal }>(`/api/repository/deals/${id}/commit`, {
      method: "POST",
      body: "{}",
    }),
  uncommitRepoDeal: (id: string) =>
    jsonRequest<{ deal: RepositoryDeal }>(`/api/repository/deals/${id}/uncommit`, {
      method: "POST",
      body: "{}",
    }),
  deleteRepoDeal: (id: string) =>
    jsonRequest<{ ok: true; deleted: { docs: number; messages: number } }>(
      `/api/repository/deals/${id}`,
      { method: "DELETE" },
    ),

  listRepoDocs: (dealId: string) =>
    jsonRequest<{ docs: RepositoryDoc[] }>(`/api/repository/deals/${dealId}/docs`),
  /**
   * Two-step upload, same pattern as review-tool attachments: sign → PUT
   * straight to GCS → complete. Doc kind / parent link are tagged afterward
   * via patchRepoDoc, since a folder upload doesn't know each file's kind.
   */
  uploadRepoDoc: async (dealId: string, file: File) => {
    const sign = await jsonRequest<{
      docId: string;
      uploadUrl: string;
      contentType: string;
      uploadToken: string;
      uploadHeaders: Record<string, string>;
    }>(`/api/repository/deals/${dealId}/docs/sign`, {
      method: "POST",
      body: JSON.stringify({
        filename: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
      }),
    });
    const putRes = await fetch(sign.uploadUrl, {
      method: "PUT",
      headers: sign.uploadHeaders,
      body: file,
    });
    if (!putRes.ok) {
      const body = await putRes.text().catch(() => "");
      throw new Error(`upload_failed ${putRes.status}: ${body.slice(0, 200)}`);
    }
    return jsonRequest<{ doc: RepositoryDoc }>(
      `/api/repository/deals/${dealId}/docs/complete`,
      { method: "POST", body: JSON.stringify({ uploadToken: sign.uploadToken }) },
    );
  },
  ackRepoBridgeFlag: (dealId: string, key: string) =>
    jsonRequest<{ flags: RepoBridgeFlag[] }>(`/api/repository/deals/${dealId}/bridge-flags/ack`, {
      method: "POST",
      body: JSON.stringify({ key }),
    }),
  detectRepoDocKinds: (dealId: string, exclude: string[] = []) =>
    jsonRequest<{
      checked: number;
      attempted: string[];
      docs: RepositoryDoc[];
      remaining: number;
      failed: number;
    }>(`/api/repository/deals/${dealId}/docs/detect-kinds`, {
      method: "POST",
      body: JSON.stringify({ exclude }),
    }),
  patchRepoDoc: (
    docId: string,
    body: Partial<{ docKind: RepoDocKind; parentDocId: string | null; docDate: number | null }>,
  ) =>
    jsonRequest<{ doc: RepositoryDoc }>(`/api/repository/docs/${docId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteRepoDoc: (docId: string) =>
    jsonRequest<{ ok: true }>(`/api/repository/docs/${docId}`, { method: "DELETE" }),
  getRepoDocUrl: (docId: string) =>
    jsonRequest<{ url: string; filename: string }>(`/api/repository/docs/${docId}/url`),

  listRepoMessages: (dealId: string) =>
    jsonRequest<{ messages: RepoMessage[] }>(`/api/repository/deals/${dealId}/messages`),
  // Pre-warms the prompt cache for this deal's chat so the first question
  // answers fast. Fire-and-forget; the backend dedupes repeat calls.
  warmRepoDealChat: (dealId: string) =>
    jsonRequest<{ warmed: boolean }>(`/api/repository/deals/${dealId}/warm`, {
      method: "POST",
      body: JSON.stringify({}),
    }),
  clearRepoMessages: (dealId: string) =>
    jsonRequest<{ ok: true; deleted: number }>(`/api/repository/deals/${dealId}/messages`, {
      method: "DELETE",
    }),

  listRepoAskMessages: () =>
    jsonRequest<{ messages: RepoMessage[] }>("/api/repository/ask/messages"),
  clearRepoAskMessages: () =>
    jsonRequest<{ ok: true; deleted: number }>("/api/repository/ask/messages", {
      method: "DELETE",
    }),
};

/**
 * Streams an assistant answer for a repository per-deal chat over SSE. Mirrors
 * `streamMessage` but for the repository's single-thread-per-deal chat (no
 * checklist). Calls onDelta with each text chunk; resolves when done.
 */
export async function streamRepoMessage(
  dealId: string,
  content: string,
  callbacks: {
    onUserMessage?: (m: RepoMessage) => void;
    onDelta?: (text: string) => void;
    onAssistantMessage?: (m: RepoMessage) => void;
    // Progress while the model is silent (loading docs, thinking).
    // Phases: preparing | sending | thinking | writing.
    onStatus?: (phase: string, detail?: string) => void;
    // Summarized reasoning streamed before the answer — display only.
    onThinking?: (text: string) => void;
    onError?: (msg: string) => void;
  },
): Promise<void> {
  const headers = new Headers(await authHeader());
  headers.set("Content-Type", "application/json");
  const res = await fetch(`/api/repository/deals/${dealId}/messages`, {
    method: "POST",
    headers,
    body: JSON.stringify({ content }),
  });
  if (!res.ok || !res.body) {
    const raw = await res.text();
    let pretty = raw;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.message === "string") pretty = parsed.message;
    } catch {
      // not JSON — keep raw
    }
    throw new Error(`${res.status} ${pretty}`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split("\n\n");
    buffer = events.pop() ?? "";
    for (const block of events) {
      const lines = block.split("\n");
      let event = "message";
      let data = "";
      for (const line of lines) {
        if (line.startsWith("event: ")) event = line.slice(7).trim();
        else if (line.startsWith("data: ")) data += line.slice(6);
      }
      if (!data) continue;
      try {
        const payload = JSON.parse(data);
        if (event === "user_message") callbacks.onUserMessage?.(payload.message);
        else if (event === "delta") callbacks.onDelta?.(payload.text);
        else if (event === "assistant_message") callbacks.onAssistantMessage?.(payload.message);
        else if (event === "status") callbacks.onStatus?.(payload.phase, payload.detail);
        else if (event === "thinking") callbacks.onThinking?.(payload.text ?? "");
        else if (event === "error") callbacks.onError?.(payload.message);
      } catch {
        // ignore malformed events
      }
    }
  }
}

/**
 * Runs Claude metadata extraction for a deal over SSE. Extraction is a single
 * long call (1–2 min for a multi-doc deal); streaming keeps the connection
 * alive past proxy idle timeouts and surfaces progress. Resolves when done.
 */
export async function streamRepoExtract(
  dealId: string,
  callbacks: {
    onStatus?: (text: string) => void;
    onDone?: (deal: RepositoryDeal) => void;
    onError?: (msg: string) => void;
  },
): Promise<void> {
  const headers = new Headers(await authHeader());
  headers.set("Content-Type", "application/json");
  const res = await fetch(`/api/repository/deals/${dealId}/extract`, {
    method: "POST",
    headers,
    body: JSON.stringify({}),
  });
  if (!res.ok || !res.body) {
    const raw = await res.text();
    let pretty = raw;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.message === "string") pretty = parsed.message;
    } catch {
      // not JSON
    }
    throw new Error(`${res.status} ${pretty}`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split("\n\n");
    buffer = events.pop() ?? "";
    for (const block of events) {
      const lines = block.split("\n");
      let event = "message";
      let data = "";
      for (const line of lines) {
        if (line.startsWith("event: ")) event = line.slice(7).trim();
        else if (line.startsWith("data: ")) data += line.slice(6);
      }
      if (!data) continue;
      try {
        const payload = JSON.parse(data);
        if (event === "status") callbacks.onStatus?.(payload.text);
        else if (event === "done") callbacks.onDone?.(payload.deal);
        else if (event === "error") callbacks.onError?.(payload.message);
      } catch {
        // ignore malformed events
      }
    }
  }
}

export interface AskCandidate {
  id: string;
  label: string;
}

/**
 * Streams a cross-deal "Ask the repository" answer. Emits richer progress
 * events than the per-deal chat: status lines, the candidate deal set, and a
 * tick per deal whose documents get read, before the final answer streams in.
 */
export async function streamRepoAsk(
  question: string,
  callbacks: {
    onUserMessage?: (m: RepoMessage) => void;
    onStatus?: (text: string) => void;
    // Summarized reasoning streamed during synthesis — display only.
    onThinking?: (text: string) => void;
    onCandidates?: (c: { total: number; shown: number; truncated?: boolean; deals: AskCandidate[] }) => void;
    onFinding?: (f: { dealId: string; label: string; applies: boolean }) => void;
    onDelta?: (text: string) => void;
    onAssistantMessage?: (m: RepoMessage) => void;
    onError?: (msg: string) => void;
  },
): Promise<void> {
  const headers = new Headers(await authHeader());
  headers.set("Content-Type", "application/json");
  const res = await fetch("/api/repository/ask", {
    method: "POST",
    headers,
    body: JSON.stringify({ question }),
  });
  if (!res.ok || !res.body) {
    const raw = await res.text();
    let pretty = raw;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.message === "string") pretty = parsed.message;
    } catch {
      // not JSON
    }
    throw new Error(`${res.status} ${pretty}`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split("\n\n");
    buffer = events.pop() ?? "";
    for (const block of events) {
      const lines = block.split("\n");
      let event = "message";
      let data = "";
      for (const line of lines) {
        if (line.startsWith("event: ")) event = line.slice(7).trim();
        else if (line.startsWith("data: ")) data += line.slice(6);
      }
      if (!data) continue;
      try {
        const payload = JSON.parse(data);
        if (event === "user_message") callbacks.onUserMessage?.(payload.message);
        else if (event === "status") callbacks.onStatus?.(payload.text);
        else if (event === "thinking") callbacks.onThinking?.(payload.text ?? "");
        else if (event === "candidates") callbacks.onCandidates?.(payload);
        else if (event === "finding") callbacks.onFinding?.(payload);
        else if (event === "delta") callbacks.onDelta?.(payload.text);
        else if (event === "assistant_message") callbacks.onAssistantMessage?.(payload.message);
        else if (event === "error") callbacks.onError?.(payload.message);
      } catch {
        // ignore malformed events
      }
    }
  }
}

/** Public share endpoints — no Firebase auth header. */
export const publicApi = {
  getInfo: async (token: string): Promise<PublicShareInfo | { status: "expired" | "revoked" } | null> => {
    const res = await fetch(`/api/public-shares/${token}/info`);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
    return res.json();
  },
  authenticate: async (token: string, password: string): Promise<{ session: string; expiresAt: number }> => {
    const res = await fetch(`/api/public-shares/${token}/auth`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    });
    if (res.status === 401) throw new Error("wrong_password");
    if (res.status === 410) throw new Error("share_unavailable");
    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
    return res.json();
  },
  getDeal: async (token: string, session: string): Promise<PublicShareDeal> => {
    const res = await fetch(`/api/public-shares/${token}/deal`, {
      headers: { Authorization: `Bearer ${session}` },
    });
    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
    return res.json();
  },
  getMessages: async (token: string, session: string): Promise<PublicShareMessage[]> => {
    const res = await fetch(`/api/public-shares/${token}/messages`, {
      headers: { Authorization: `Bearer ${session}` },
    });
    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
    const json = (await res.json()) as { messages: PublicShareMessage[] };
    return json.messages;
  },
};

/**
 * Streams an assistant response over SSE. Calls onDelta with each text chunk;
 * resolves with the final assistant message when streaming completes.
 *
 * The backend decides for itself whether this is a first-pass review
 * (no checklist items yet) or a follow-up — there is no explicit
 * kickoff flag.
 */
export async function streamMessage(
  dealId: string,
  reviewId: string,
  content: string,
  callbacks: {
    onUserMessage?: (m: Message) => void;
    onDelta?: (text: string) => void;
    onAssistantMessage?: (m: Message) => void;
    onChecklistGenerating?: (count: number) => void;
    // Progress while the model is silent (loading docs, thinking).
    // Phases: preparing | sending | thinking | writing | continuing.
    onStatus?: (phase: string, detail?: string) => void;
    // Summarized reasoning streamed before the answer — display only.
    onThinking?: (text: string) => void;
    onError?: (msg: string) => void;
  },
  // Abort to stop generation. Aborting closes the connection, which the
  // backend detects and uses to abort the Anthropic stream — so the Stop
  // button actually halts billing, not just the client-side display.
  signal?: AbortSignal,
): Promise<void> {
  const headers = new Headers(await authHeader());
  headers.set("Content-Type", "application/json");
  const res = await fetch(`/api/messages/${dealId}/${reviewId}`, {
    method: "POST",
    headers,
    body: JSON.stringify({ content }),
    signal,
  });
  if (!res.ok || !res.body) {
    // Try to surface the backend's `message` field (e.g. for the
    // 409 kickoff-race response) instead of the raw JSON blob.
    const raw = await res.text();
    let pretty = raw;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.message === "string") pretty = parsed.message;
    } catch {
      // not JSON — keep raw
    }
    throw new Error(`${res.status} ${pretty}`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split("\n\n");
    buffer = events.pop() ?? "";
    for (const block of events) {
      const lines = block.split("\n");
      let event = "message";
      let data = "";
      for (const line of lines) {
        if (line.startsWith("event: ")) event = line.slice(7).trim();
        else if (line.startsWith("data: ")) data += line.slice(6);
      }
      if (!data) continue;
      try {
        const payload = JSON.parse(data);
        if (event === "user_message") callbacks.onUserMessage?.(payload.message);
        else if (event === "delta") callbacks.onDelta?.(payload.text);
        else if (event === "assistant_message") callbacks.onAssistantMessage?.(payload.message);
        else if (event === "checklist_generating")
          callbacks.onChecklistGenerating?.(payload.count ?? 0);
        else if (event === "status") callbacks.onStatus?.(payload.phase, payload.detail);
        else if (event === "thinking") callbacks.onThinking?.(payload.text ?? "");
        else if (event === "error") callbacks.onError?.(payload.message);
      } catch {
        // ignore malformed events
      }
    }
  }
}
